// /home/bigbrain/projects/smashparty/src/client/input/deadzone.ts
import type { Vec2 } from './types';

/** Spec §10 line 794: radial deadzone inner 0.15 (below this → zero). */
export const DEADZONE_INNER = 0.15;
/** Spec §10 line 794: radial deadzone outer 0.98 (above this → unit vector). */
export const DEADZONE_OUTER = 0.98;
/** Spec §10 line 795: exponential response curve exponent 2.2. */
export const RESPONSE_EXPONENT = 2.2;

/**
 * Applies a radial deadzone and exponential response curve to a raw stick Vec2.
 *
 * Spec §10: "natural diagonal movement" — direction is preserved exactly through
 * the curve; only the magnitude is shaped.
 *
 * Three zones:
 *  - magnitude < DEADZONE_INNER  → return {x:0, y:0} (inner dead zone)
 *  - magnitude > DEADZONE_OUTER  → return unit vector (outer full-deflection clamp)
 *  - otherwise (active band)     → rescale magnitude to [0,1] then apply
 *    curve: curved = ((mag - INNER) / (OUTER - INNER))^EXPONENT
 *    and multiply the unit direction by curved magnitude.
 *
 * Note on inner boundary (mag === DEADZONE_INNER):
 *   The condition is `< DEADZONE_INNER`, so exactly 0.15 enters the active band.
 *   The curve formula evaluates to 0^2.2 = 0 at that point, so the output is
 *   still {0,0} — but the reason is the curve, not the deadzone cut.
 *
 * Note on outer boundary (mag === DEADZONE_OUTER):
 *   The condition is `> DEADZONE_OUTER`, so exactly 0.98 enters the active band.
 *   The curve formula evaluates to 1^2.2 = 1 at that point, giving a unit vector
 *   via the curve path (equivalent to, but distinct from, the outer clamp path).
 *
 * Zero input (mag === 0) is handled by the inner deadzone branch (0 < 0.15),
 * so no division-by-zero can occur.
 */
export function applyDeadzoneAndCurve(raw: Vec2): Vec2 {
  const mag = Math.hypot(raw.x, raw.y);

  if (mag < DEADZONE_INNER) {
    return { x: 0, y: 0 };
  }

  if (mag > DEADZONE_OUTER) {
    return { x: raw.x / mag, y: raw.y / mag };
  }

  // Active band: rescale magnitude to [0, 1] and apply exponential curve.
  const rescaled = (mag - DEADZONE_INNER) / (DEADZONE_OUTER - DEADZONE_INNER);
  const curved = rescaled ** RESPONSE_EXPONENT;

  // Preserve direction: unit vector × curved magnitude.
  return { x: (raw.x / mag) * curved, y: (raw.y / mag) * curved };
}
